import { NormalizeOAS, OASRequestParams, OASResponse } from 'fets';
import type assetLibraryOpenApi from '../asset-library-open-api';
import { AssetLibraryFetsApiClient } from '../asset-library-fets-client';
import { assertNever } from '../utils';
import { Guid } from '../../api/public/utils/Guid';

const PATH = '/api/v2/asset-directory/company/{companyId}/move/{directoryId}';

/** The library root, which the route accepts as a target so a folder can be un-nested. */
const ROOT_DIRECTORY_ID = '00000000-0000-0000-0000-000000000000';

type Params = {
  companyId: Guid;
  directoryId: Guid;
  /** `null` moves the folder to the top level. */
  newParentId: Guid | null;
};

/**
 * Discriminated rather than thrown: every refusal here is an ordinary outcome of a drag the user is
 * allowed to attempt. Only the transport failing is exceptional.
 */
export type MoveFolderResult =
  | { status: 'moved'; id: Guid; previousParentId: Guid | null; newParentId: Guid | null }
  /** The folder, or the target, is fixed. `detail` says which. */
  | { status: 'fixed'; detail: string }
  /** Either end is gone or archived. */
  | { status: 'notFound'; detail: string }
  /** The target is the folder itself or one of its descendants. */
  | { status: 'insideOwnSubtree'; detail: string };

export type ApiFunction = (params: Params, signal?: AbortSignal) => Promise<MoveFolderResult>;

export const createMoveAssetFolder = (apiClient: AssetLibraryFetsApiClient): ApiFunction => {
  const moveAssetFolder = async (params: Params, signal?: AbortSignal): Promise<MoveFolderResult> => {
    const response = await apiClient[PATH].put({
      ...convertToRequest(params),
      ...(signal && { signal }),
    });

    return convertResponse(response);
  };

  return moveAssetFolder;
};

type Request = OASRequestParams<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'put'>;
type Response = OASResponse<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'put'>;

const convertToRequest = (params: Params): Request => ({
  params: { companyId: params.companyId, directoryId: params.directoryId },
  // The root is the empty guid, so un-nesting needs no lookup.
  json: { newParentId: params.newParentId ?? ROOT_DIRECTORY_ID },
});

const convertResponse = async (response: Response): Promise<MoveFolderResult> => {
  const responseStatus = response.status;

  if (responseStatus === 200) {
    const data = await response.json();
    return {
      status: 'moved',
      id: data.id,
      previousParentId: data.previousParentId ?? null,
      newParentId: data.newParentId ?? null,
    };
  }

  if (responseStatus === 404) {
    return { status: 'notFound', detail: await problemDetail(response) };
  }

  if (responseStatus === 409) {
    return { status: 'insideOwnSubtree', detail: await problemDetail(response) };
  }

  if (responseStatus === 403) {
    return { status: 'fixed', detail: await problemDetail(response) };
  }

  return assertNever(responseStatus);
};

/** The `detail` of a problem document, or a bare fallback when the body is not one. */
const problemDetail = async (response: Response): Promise<string> => {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && 'detail' in body && typeof body.detail === 'string') {
      return body.detail;
    }
  } catch {
    // A refusal with no body is still a refusal; the status already carried the meaning.
  }
  return 'The folder could not be moved.';
};
